import "./styles.css";
import type { StationRole } from "../shared/protocol";
import { meterPercent, NoiseAlert } from "./noise";
import { MonitorSession } from "./monitor";
import { enablePush, preparePush, registerServiceWorker, type PushState } from "./push";
import { ScreenWakeLock, type WakeLockState } from "./wake-lock";

const appRoot = document.querySelector<HTMLElement>("#app");
if (!appRoot) throw new Error("App root was not found.");
const root: HTMLElement = appRoot;

let session: MonitorSession | null = null;
let screenWakeLock: ScreenWakeLock | null = null;
let alertTimer: number | null = null;
let lastNoiseTimer: number | null = null;
let latestLevel = -100;
let lastNoiseAt: number | null = null;

void boot();

async function boot(): Promise<void> {
  void registerServiceWorker();
  renderLoading();
  try {
    const response = await fetch("/api/session", { cache: "no-store" });
    const body = (await response.json()) as { authenticated?: boolean };
    if (body.authenticated) renderRolePicker();
    else renderLogin();
  } catch {
    renderFatal("The app could not reach the server. Check your connection and reload.");
  }
}

function renderLoading(): void {
  setThemeColor("#f7f3ed");
  root.className = "app-shell";
  root.innerHTML = `<section class="card centered"><div class="brand-mark">◡</div><p class="eyebrow">Baby monitor</p><h1>Waking up…</h1></section>`;
}

function renderLogin(): void {
  setThemeColor("#f7f3ed");
  stopSession();
  root.className = "app-shell";
  root.innerHTML = `
    <section class="card auth-card">
      <div class="brand-mark" aria-hidden="true">◡</div>
      <p class="eyebrow">Private monitor</p>
      <h1>Welcome home</h1>
      <p class="intro">Enter your monitor password to continue.</p>
      <form id="login-form">
        <label for="password">Password</label>
        <input id="password" name="password" type="password" autocomplete="current-password" required autofocus maxlength="256" />
        <p id="login-error" class="error-message" role="alert"></p>
        <button class="primary" type="submit">Unlock monitor</button>
      </form>
    </section>`;

  const form = getElement<HTMLFormElement>("login-form");
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    const button = form.querySelector<HTMLButtonElement>("button")!;
    const error = getElement<HTMLElement>("login-error");
    const password = new FormData(form).get("password");
    button.disabled = true;
    button.textContent = "Checking…";
    error.textContent = "";
    try {
      const response = await fetch("/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      const body = (await response.json()) as { error?: string };
      if (!response.ok) throw new Error(body.error || "Login failed.");
      renderRolePicker();
    } catch (cause) {
      error.textContent = cause instanceof Error ? cause.message : "Login failed.";
      button.disabled = false;
      button.textContent = "Unlock monitor";
    }
  });
}

function renderRolePicker(message = ""): void {
  setThemeColor("#f7f3ed");
  stopSession();
  root.className = "app-shell";
  root.innerHTML = `
    <section class="card role-card">
      <div class="top-row"><div><p class="eyebrow">Baby monitor</p><h1>Choose this device</h1></div><button id="logout" class="text-button">Lock</button></div>
      ${message ? `<p class="notice warning" id="role-message"></p>` : ""}
      <div class="role-grid">
        <button id="baby-role" class="role-choice">
          <span class="role-icon baby-icon" aria-hidden="true">●</span>
          <strong>Baby station</strong>
          <span>Share this device’s microphone</span>
        </button>
        <button id="parent-role" class="role-choice">
          <span class="role-icon parent-icon" aria-hidden="true">◖</span>
          <strong>Parent station</strong>
          <span>Listen and watch the noise level</span>
        </button>
      </div>
      <p class="privacy-note">Audio goes directly between your devices and is never recorded.</p>
    </section>`;
  if (message) getElement("role-message").textContent = message;
  getElement("baby-role").addEventListener("click", () => void startStation("baby"));
  getElement("parent-role").addEventListener("click", () => void startStation("parent"));
  getElement("logout").addEventListener("click", async () => {
    await fetch("/api/logout", { method: "POST" });
    renderLogin();
  });
}

async function startStation(role: StationRole): Promise<void> {
  renderStation(role);
  const status = getElement<HTMLElement>("connection-status");
  const meterFill = getElement<HTMLElement>("meter-fill");
  const levelText = getElement<HTMLElement>("level-text");
  const playbackButton = document.querySelector<HTMLButtonElement>("#start-listening");
  let detector: NoiseAlert | null = null;
  let localAlertActive = false;
  let pushEnabled = false;
  let currentThreshold = -40;

  screenWakeLock = new ScreenWakeLock(renderWakeLockState);
  const wakeButton = getElement<HTMLButtonElement>("wake-lock");
  wakeButton.addEventListener("click", () => void screenWakeLock?.toggle());
  void screenWakeLock.enable();

  if (role === "parent") {
    const thresholdInput = getElement<HTMLInputElement>("threshold");
    const stored = Number(localStorage.getItem("noise-threshold-db"));
    const initial = Number.isFinite(stored) && stored >= -60 && stored <= -10 ? stored : -40;
    currentThreshold = initial;
    thresholdInput.value = String(initial);
    detector = new NoiseAlert(initial);
    updateThresholdLabel(initial);
    thresholdInput.addEventListener("input", () => {
      const threshold = Number(thresholdInput.value);
      detector!.thresholdDb = threshold;
      detector!.reset();
      localStorage.setItem("noise-threshold-db", String(threshold));
      updateThresholdLabel(threshold);
      currentThreshold = threshold;
      session?.updateAlertSettings(currentThreshold, pushEnabled);
    });
    alertTimer = window.setInterval(() => {
      const alerting = detector!.observe(latestLevel);
      root.classList.toggle("noise-alert", alerting);
      if (alerting && !localAlertActive) {
        lastNoiseAt = Date.now();
        updateLastNoise();
      }
      localAlertActive = alerting;
    }, 100);
    lastNoiseTimer = window.setInterval(updateLastNoise, 30_000);
  }

  session = new MonitorSession(role, {
    onStatus(message, kind = "normal") {
      status.textContent = message;
      status.dataset.kind = kind;
    },
    onLevel(db) {
      latestLevel = db;
      meterFill.style.width = `${meterPercent(db)}%`;
      levelText.textContent = db <= -99 ? "Quiet" : `${Math.round(db)} dB`;
    },
    onError(message) {
      showStationError(message);
    },
    onRoleUnavailable(unavailableRole) {
      renderRolePicker(`The ${unavailableRole} station is already in use on another device.`);
    },
    onAuthenticationLost() {
      renderLogin();
    },
    onPlaybackBlocked(blocked) {
      if (playbackButton) playbackButton.hidden = !blocked;
      const liveStatus = document.querySelector<HTMLElement>("#live-audio-status");
      if (liveStatus) liveStatus.textContent = blocked ? "Tap below to hear the nursery" : "Live audio is on";
    },
    onNoiseDetected(at) {
      lastNoiseAt = at;
      updateLastNoise();
    },
  });

  playbackButton?.addEventListener("click", () => void session?.resumeAudio());
  getElement("change-role").addEventListener("click", () => renderRolePicker());

  try {
    await session.start();
    if (role === "parent") {
      const setup = await preparePush();
      pushEnabled = setup.state === "enabled";
      session.updateAlertSettings(currentThreshold, pushEnabled);
      renderPushState(setup.state);
      const enableButton = document.querySelector<HTMLButtonElement>("#enable-notifications");
      enableButton?.addEventListener("click", async () => {
        if (!setup.publicKey) return;
        enableButton.disabled = true;
        enableButton.textContent = "Enabling…";
        try {
          const nextState = await enablePush(setup.publicKey);
          pushEnabled = nextState === "enabled";
          session?.updateAlertSettings(currentThreshold, pushEnabled);
          renderPushState(nextState);
        } catch {
          renderPushState("unconfigured", "Notifications could not be enabled. Please try again.");
        }
      });
    }
  } catch (cause) {
    showStationError(cause instanceof Error ? cause.message : "The station could not start.", true);
  }
}

function renderStation(role: StationRole): void {
  latestLevel = -100;
  const isBaby = role === "baby";
  setThemeColor(isBaby ? "#050807" : "#f7f3ed", isBaby);
  root.className = `app-shell station-shell ${role}-shell`;
  root.innerHTML = `
    <section class="station-card">
      <header class="station-header">
        <div><p class="eyebrow">${isBaby ? "Baby station" : "Parent station"}</p><h1>${isBaby ? "Listening to the room" : "Nursery sound"}</h1></div>
        <span class="live-dot" aria-hidden="true"></span>
      </header>
      <div class="status-pill"><span class="status-dot"></span><span id="connection-status">Starting…</span></div>
      <button id="wake-lock" class="wake-lock" type="button">Keeping screen awake…</button>
      <div class="sound-orbit ${isBaby ? "baby-orbit" : "parent-orbit"}" aria-hidden="true">
        <div class="orbit-ring ring-one"></div><div class="orbit-ring ring-two"></div><div class="orbit-core">${isBaby ? "●" : "◖"}</div>
      </div>
      <div class="meter-block">
        <div class="meter-labels"><span>Sound level</span><strong id="level-text">Quiet</strong></div>
        <div class="meter-track"><div id="meter-fill" class="meter-fill"></div><div class="threshold-marker" id="threshold-marker"></div></div>
      </div>
      ${isBaby ? babyControls() : parentControls()}
      <p id="station-error" class="error-message station-error" role="alert"></p>
      <button id="change-role" class="secondary">Stop and change station</button>
    </section>`;
}

function babyControls(): string {
  return `<div class="notice"><strong>Keep this page open</strong><span>Leave this device awake and connected to power. iOS may stop the microphone when Safari is locked or in the background.</span></div>`;
}

function parentControls(): string {
  return `
    <audio id="remote-audio" class="remote-audio" autoplay playsinline></audio>
    <div class="activity-row">
      <div><span>Last noise detected</span><strong id="last-noise">No noise detected yet</strong></div>
      <div class="live-listening"><span class="listening-dot"></span><strong id="live-audio-status">Starting live audio…</strong></div>
    </div>
    <div class="threshold-control">
      <div><label for="threshold">Alert sensitivity</label><span id="threshold-label">−40 dB</span></div>
      <input id="threshold" type="range" min="-60" max="-10" step="1" />
      <div class="range-labels"><span>More sensitive</span><span>Less sensitive</span></div>
    </div>
    <div id="push-panel" class="push-panel">
      <div><strong>Background alerts</strong><span id="push-status">Checking notification support…</span></div>
      <button id="enable-notifications" class="small-button" hidden>Enable</button>
    </div>
    <button id="start-listening" class="primary compact" hidden>Tap to start listening</button>
    <div class="alert-copy"><strong>Noise detected</strong><span>The room is louder than your alert level.</span></div>`;
}

function updateLastNoise(): void {
  const element = document.querySelector<HTMLElement>("#last-noise");
  if (!element || !lastNoiseAt) return;
  const elapsedSeconds = Math.max(0, Math.floor((Date.now() - lastNoiseAt) / 1_000));
  let relative: string;
  if (elapsedSeconds < 10) relative = "Just now";
  else if (elapsedSeconds < 60) relative = `${elapsedSeconds}s ago`;
  else if (elapsedSeconds < 3_600) relative = `${Math.floor(elapsedSeconds / 60)}m ago`;
  else if (elapsedSeconds < 86_400) relative = `${Math.floor(elapsedSeconds / 3_600)}h ago`;
  else relative = new Date(lastNoiseAt).toLocaleDateString([], { month: "short", day: "numeric" });
  const time = new Date(lastNoiseAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  element.textContent = `${relative} · ${time}`;
}

function renderWakeLockState(state: WakeLockState): void {
  const button = document.querySelector<HTMLButtonElement>("#wake-lock");
  if (!button) return;
  const labels: Record<WakeLockState, string> = {
    active: "Screen stays awake",
    inactive: "Keep screen awake",
    unsupported: "Wake lock unavailable",
    failed: "Tap to keep screen awake",
  };
  button.textContent = labels[state];
  button.dataset.state = state;
  button.disabled = state === "unsupported";
  button.title = state === "unsupported" || state === "failed"
    ? "If this keeps failing, turn off Auto-Lock in the device display settings."
    : "Tap to toggle screen wake lock.";
}

function renderPushState(state: PushState, override = ""): void {
  const status = document.querySelector<HTMLElement>("#push-status");
  const button = document.querySelector<HTMLButtonElement>("#enable-notifications");
  if (!status || !button) return;
  const messages: Record<PushState, string> = {
    enabled: "Enabled. You’ll be notified when this app is in the background.",
    available: "Get an iPhone alert when sustained noise is detected.",
    denied: "Notifications are blocked. Enable them in iPhone Settings.",
    "needs-install": "In Safari, tap Share → Add to Home Screen, then open the monitor from its icon.",
    unsupported: "This browser does not support background notifications.",
    unconfigured: "Push notifications are not configured on the server yet.",
  };
  status.textContent = override || messages[state];
  button.hidden = state !== "available";
  button.disabled = false;
  button.textContent = "Enable";
}

function updateThresholdLabel(threshold: number): void {
  getElement("threshold-label").textContent = `${threshold} dB`;
  const marker = getElement<HTMLElement>("threshold-marker");
  marker.style.left = `${meterPercent(threshold)}%`;
  marker.hidden = false;
}

function showStationError(message: string, fatal = false): void {
  const error = document.querySelector<HTMLElement>("#station-error");
  if (error) error.textContent = message;
  if (fatal) getElement("connection-status").textContent = "Could not start";
}

function renderFatal(message: string): void {
  setThemeColor("#f7f3ed");
  root.className = "app-shell";
  root.innerHTML = `<section class="card centered"><p class="eyebrow">Baby monitor</p><h1>Something went wrong</h1><p id="fatal-message" class="intro"></p><button id="reload" class="primary">Reload</button></section>`;
  getElement("fatal-message").textContent = message;
  getElement("reload").addEventListener("click", () => location.reload());
}

function stopSession(): void {
  void screenWakeLock?.stop();
  screenWakeLock = null;
  session?.stop();
  session = null;
  latestLevel = -100;
  lastNoiseAt = null;
  root.classList.remove("noise-alert");
  if (alertTimer !== null) window.clearInterval(alertTimer);
  alertTimer = null;
  if (lastNoiseTimer !== null) window.clearInterval(lastNoiseTimer);
  lastNoiseTimer = null;
}

function getElement<T extends HTMLElement = HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) throw new Error(`Missing #${id}`);
  return element as T;
}

function setThemeColor(color: string, darkDocument = false): void {
  document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')?.setAttribute("content", color);
  document.documentElement.classList.toggle("baby-document", darkDocument);
  document.body.classList.toggle("baby-document", darkDocument);
}
